import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useAuth, useBoolean, useQueryParams, useRoles } from 'hooks'
import { useUnloadBeacon } from './use-unload-beacon'

import { closeVideoEditor } from 'ducks/actions'

import { PlaybookType } from 'app/types'
import { globalProperties, logToAnalytics, playbookToAnalyticsProps, userProperties } from 'modules'

type Props = {
    playbook?: PlaybookType
    playlist?: PlaybookType
    isEditMode?: boolean
    videoElement: HTMLVideoElement | null
}

export const useVideoReporter = ({ playbook, playlist, isEditMode, videoElement }: Props) => {
    const dispatch = useDispatch()

    // to track that active playbooks of playlist was changed
    const prevPlaybook = useRef(playbook)

    const willUnmount = useRef<boolean>(false)

    const sent = useBoolean()
    const setSent = sent.set

    const [videoStatus, setVideoStatus] = useState('stopped')
    const [timeoutId, setTimeoutId] = useState<number>(0)

    const params = useQueryParams()
    const origin = params.get('origin') || ''
    const utmCampaign = params.get('track_link_name') || ''
    const campaignId = params.get('track_link_id') || ''
    const spaceId = params.get('spaceId') || ''

    const [segments, setSegments] = useState({})

    const { isSuperAdmin } = useRoles()
    const { uid } = useAuth()

    const isOwner = playbook && uid === playbook.creator_uid

    const { isAnonymous } = useSelector(state => state.firebase.auth)

    const { duration } = useSelector(state => state.videoEditor)

    const segmentsWatched = Object.keys(segments).length

    const videoMetaData = useMemo(() => {
        if (!duration)
            return {
                percentage: 0,
                totalSegments: 0,
                segmentsWatched: 0
            }

        const totalSegments = Math.floor(secondsToSegments(duration))

        const percentage = Math.min(100, Math.floor((segmentsWatched / totalSegments) * 100))

        return {
            percentage,
            totalSegments,
            segmentsWatched
        }
    }, [duration, segmentsWatched])

    const playlistData = useMemo(
        () =>
            playlist
                ? {
                      'playlist-id': playlist.id,
                      'playlist-title': playlist.title
                  }
                : {},
        [playlist]
    )

    const shouldSend = !isSuperAdmin && !isOwner && segmentsWatched > 1 && !isEditMode

    useUnloadBeacon(
        {
            url: '/tk/v1/eov',
            data: {
                eventData: {
                    event: 'videoPlay',
                    url: window.location.href,
                    videoMetaData,
                    origin,
                    ...(spaceId ? { spaceId } : {}),
                    ...playlistData,
                    ...playbookToAnalyticsProps(
                        {
                            ...(playbook as PlaybookType),
                            campaignId,
                            utmCampaign
                        },
                        isAnonymous
                    )
                },
                userProperties,
                globalProperties
            }
        },
        shouldSend && sent.isFalse
    )

    const trackVideoPlay = useCallback(
        (resetProgress?: boolean) => {
            if (sent.isTrue || !shouldSend || !videoMetaData.percentage) return

            logToAnalytics('videoPlay', {
                videoMetaData,
                ...playlistData,
                spaceId,
                origin,
                ...playbookToAnalyticsProps(
                    {
                        ...(prevPlaybook.current || (playbook as PlaybookType)),
                        campaignId,
                        utmCampaign
                    },
                    isAnonymous
                )
            })

            if (resetProgress) {
                setSegments({})
                setSent(false)
            } else {
                setSent(true)
            }
        },
        [
            sent.isTrue,
            shouldSend,
            videoMetaData,
            playlistData,
            spaceId,
            playbook,
            campaignId,
            utmCampaign,
            isAnonymous,
            origin,
            setSent
        ]
    )

    // Listeners to get video play status
    useEffect(() => {
        // do not do anything until user starts the video
        if (!videoElement) return

        const handleUpdate = (event: Event) => {
            if (!event.target) return
            const time = (event.target as HTMLVideoElement).currentTime

            setSegments(prev => ({
                ...prev,
                [secondsToSegments(time)]: true
            }))
        }

        videoElement.addEventListener('playing', () => setVideoStatus('playing'))
        videoElement.addEventListener('pause', () => setVideoStatus('stopped'))
        videoElement.addEventListener('timeupdate', handleUpdate)

        return () => {
            videoElement.removeEventListener('playing', () => setVideoStatus('playing'))
            videoElement.removeEventListener('pause', () => setVideoStatus('stopped'))
            videoElement.removeEventListener('timeupdate', handleUpdate)
        }
    }, [videoElement])

    // Send videoPlay when playlist's active video changes.
    // For example, you watched 40% of 1st video and then selected 2nd -
    // we need to track those 40% of the 1st playbook
    useEffect(() => {
        if (!playbook || !videoMetaData.percentage) return

        if (playbook.id !== prevPlaybook.current?.id) {
            trackVideoPlay(true)
            prevPlaybook.current = playbook
        }
    }, [playbook, trackVideoPlay, videoMetaData.percentage])

    // Send videoPlay if user switched to another tab for more than 30min
    useEffect(() => {
        // don't do anything while it's playing or no percentage
        if (!videoMetaData?.percentage) return

        // remove timeout and listener if api call has already been sent or user clicked on play
        if (sent.isTrue || videoStatus === 'playing') {
            if (timeoutId) {
                window.onblur = null
                clearTimeout(timeoutId)
                setTimeoutId(0)
            }
        }

        // add timeout on blur
        if (sent.isFalse && videoStatus !== 'playing') {
            window.onblur = () => {
                // if we have active timeout - we need to clear it before start a new one
                if (timeoutId) {
                    window.onblur = null
                    clearTimeout(timeoutId)
                    setTimeoutId(0)
                }

                const id = setTimeout(() => {
                    trackVideoPlay()
                    clearTimeout(id)
                }, 60 * 1000 * 30) // 30 minutes

                // React doesn't have built in Timeout type
                setTimeoutId(id as unknown as number)
            }
        }
    }, [
        videoMetaData?.percentage,
        videoStatus,
        sent.isFalse,
        sent.isTrue,
        trackVideoPlay,
        timeoutId
    ])

    // Send videoPlay on video ends
    useEffect(() => {
        if (
            // We need to make sure that user watched at least 1% on video ends
            videoMetaData.percentage !== 0 &&
            Math.floor(videoElement?.currentTime || 0) === Math.floor(duration) &&
            duration
        ) {
            trackVideoPlay()
        }
    }, [duration, trackVideoPlay, videoElement?.currentTime, videoMetaData.percentage])

    // Need to prevent multiple api calls on route change (next useEffect)
    useEffect(() => {
        return () => {
            willUnmount.current = true
        }
    }, [])

    // Send videoPlay on route change
    useEffect(() => {
        return () => {
            if (willUnmount.current && videoMetaData?.percentage) {
                trackVideoPlay()
                // reset video current time and onBlur listener + timeout to avoid sending unnecessary api calls
                dispatch(closeVideoEditor())
                window.onblur = null
                clearTimeout(timeoutId)
            }
        }
        // It should be run only once - on component unmount
    }, [dispatch, timeoutId, trackVideoPlay, videoMetaData])
}

const secondsToSegments = (seconds: number) => Math.floor(seconds / 5) + 1
